// components/shareable/searchInput.js
import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react'; // Import X icon
import { useSearch } from '../../context/SearchContext';

// Define placeholders locally
const placeholders = {
  'home': 'Search users... by (NAME or EMAIL)',           // /home - Users page
  'quotes': 'Search quotes... by (Quote #, Customer Name/Email)',  // /quotes
  'customers': 'Search customers... by (NAME or EMAIL)',  // /customers
  'product': 'Search products...',     // /product - Products page
  'saw': 'Search saws... by (BRAND or MODEL)',             // /saw - Saws page
  'productsaw': 'Search saw products... by (BRAND or MODEL)', // /productsaw - Saws products page
  '': 'Search...'  // Default fallback
};

const SearchInput = ({ className = '', page = '', debounceDelay = 300 }) => {
  const { searchQuery, currentPage, updateSearch } = useSearch();
  const [inputValue, setInputValue] = useState(searchQuery);
  
  // Get placeholder based on current page
  const placeholder = placeholders[page] || placeholders[''];

  // Update local state when searchQuery changes from outside
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  // Set page context when page prop changes
  useEffect(() => {
    if (page && page !== currentPage) {
      updateSearch(searchQuery, page);
    }
  }, [page, searchQuery, currentPage, updateSearch]);

  // Debounced update to context
  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue !== searchQuery) {
        updateSearch(inputValue, page);
      }
    }, debounceDelay);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, debounceDelay, searchQuery, updateSearch, page]);

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  // Function to clear the search input
  const handleClear = () => {
    setInputValue('');
    updateSearch('', page);
  };

  return (
    <div className={`relative w-full max-w-md ${className}`}>
      <div className="absolute inset-y-0 left-0 flex items-center pl-3">
        <Search size={16} className="text-gray-400" />
      </div>
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        className="w-full pl-10 pr-10 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition text-sm"
      />
      {/* Clear button - only shown when there's text */}
      {inputValue && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <button
            onClick={handleClear}
            className="text-gray-400 hover:text-red-600 focus:outline-none"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchInput;
